import React, { useState } from 'react';

/**
 * Emoji/Face Scale - Wong-Baker FACES style pain scale.
 *
 * Used in pediatric trials, cognitive impairment studies, and any context
 * where verbal/numeric scales are inappropriate. Each face represents a
 * level on the ordinal scale with a corresponding label.
 *
 * Usage: responseConfig.layout = 'emoji'
 */

// Standard face scale: 6 faces from happy to crying (Wong-Baker standard)
const DEFAULT_FACES = [
  { emoji: '😊', label: 'No hurt', value: '0', color: '#22c55e' },
  { emoji: '🙂', label: 'Hurts a little', value: '1', color: '#84cc16' },
  { emoji: '😐', label: 'Hurts a little more', value: '2', color: '#eab308' },
  { emoji: '🙁', label: 'Hurts even more', value: '3', color: '#f97316' },
  { emoji: '😢', label: 'Hurts a whole lot', value: '4', color: '#ef4444' },
  { emoji: '😭', label: 'Hurts worst', value: '5', color: '#dc2626' },
];

const EmojiFaceScale = ({
  options = [],
  value = null,
  onChange,
  disabled = false,
  faceSize = 48,
  fontSize = 12,
  showLabels = true,
  showValues = true,
  className = '',
}) => {
  const [selectedValue, setSelectedValue] = useState(value);

  // Use provided options or fall back to default faces
  const faces = options.length > 0
    ? options.map((opt, i) => ({
        emoji: DEFAULT_FACES[Math.min(i, DEFAULT_FACES.length - 1)]?.emoji || '😐',
        label: typeof opt === 'object' ? opt.label : opt,
        value: typeof opt === 'object' ? opt.value : String(i),
        color: DEFAULT_FACES[Math.min(i, DEFAULT_FACES.length - 1)]?.color || '#94a3b8',
      }))
    : DEFAULT_FACES;

  const handleSelect = (val) => {
    if (disabled) return;
    setSelectedValue(val);
    if (onChange) onChange(val);
  };

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 8,
        width: '100%',
      }}
    >
      {faces.map((face, i) => {
        const isSelected = String(selectedValue) === String(face.value);

        return (
          <div
            key={i}
            onClick={() => handleSelect(face.value)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 4,
              cursor: disabled ? 'default' : 'pointer',
              flex: 1,
              minWidth: 0,
            }}
          >
            {/* Face circle */}
            <div
              style={{
                width: faceSize,
                height: faceSize,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: faceSize * 0.6,
                backgroundColor: isSelected ? face.color + '20' : '#f8fafc',
                border: `3px solid ${isSelected ? face.color : '#e2e8f0'}`,
                transition: 'all 0.15s ease',
                transform: isSelected ? 'scale(1.1)' : 'scale(1)',
              }}
            >
              {face.emoji}
            </div>

            {/* Value label */}
            {showValues && (
              <span style={{
                fontSize: fontSize + 2,
                fontWeight: '700',
                color: isSelected ? face.color : '#64748b',
                fontFamily: 'system-ui, sans-serif',
              }}>
                {face.value}
              </span>
            )}

            {/* Text label */}
            {showLabels && (
              <span style={{
                fontSize,
                color: isSelected ? '#1e293b' : '#94a3b8',
                fontFamily: 'system-ui, sans-serif',
                textAlign: 'center',
                lineHeight: 1.2,
                maxWidth: faceSize + 20,
                wordWrap: 'break-word',
              }}>
                {face.label}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default EmojiFaceScale;
